/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useRef } from 'react';
import logger from '../lib/logger';

/**
 * BlueprintHero — animated SVG hero illustration that varies by service slug.
 *
 * Props:
 *   variant      — 'survey' | 'mep' | 'bim' | 'structural' | 'gis' | 'default'
 *   accent       — primary stroke color (green)
 *   accentAlt    — secondary stroke color (orange)
 *   density      — number of items in default skyline
 *   className    — wrapper classes
 */
const VARIANT_FOR_SLUG = {
  // Survey & site
  'land-surveying': 'survey',
  'land-development': 'survey',

  // GIS / mapping / environmental contours
  'gis-geospatial': 'gis',
  'environmental-engineering': 'gis',

  // MEP / facility
  'mep-engineering': 'mep',
  'facility-management': 'mep',

  // BIM / 3D / architecture
  'bim-services': 'bim',
  '3d-modeling': 'bim',
  'architectural': 'bim',

  // Structural & steel
  'structural-engineering': 'structural',
  'steel-detailing': 'structural',

  // Plant / process / energy
  'plant-engineering': 'plant',
  'process-equipment': 'plant',
  'oil-gas-energy': 'plant',

  // Civil — roads, drainage, utilities
  'civil-engineering': 'civil',
  'transportation': 'civil',
  'water-wastewater': 'civil',

  // Manufacturing — CNC / sheet-metal parts
  'mechanical-engineering': 'manufacturing',
  'manufacturing': 'manufacturing',

  // Graphics / rendering / publishing
  'graphics-services': 'graphics',
  'rendering-animation': 'graphics',
  'premedia-services': 'graphics',
  'publishing-services': 'graphics',

  // Signage
  'signage-engineering': 'signage',

  // Millwork / construction engineering / general design docs
  'lumber-millwork': 'millwork',
  'construction-engineering': 'millwork',
  'engineering-designs': 'millwork',

  // Geotechnical — soil strata cross-section
  'geotechnical-engineering': 'geotech'
};

const animatePaths = (svg) => {
  if (!svg) return;
  const paths = svg.querySelectorAll('path,line,polyline,polygon,circle,rect,ellipse');
  paths.forEach((p, i) => {
    try {
      if (typeof p.getTotalLength !== 'function') return;
      const len = p.getTotalLength();
      if (!len) return;
      p.style.strokeDasharray = `${len}`;
      p.style.strokeDashoffset = `${len}`;
      p.style.animation = `bp-draw 1700ms cubic-bezier(0.65, 0, 0.35, 1) forwards`;
      p.style.animationDelay = `${i * 30}ms`;
    } catch (err) { logger.debug('[BlueprintHero] getTotalLength failed on non-rendered path:', err); }
  });
};

const SurveyHero = ({ accent, accentAlt }) => {
  const svgRef = useRef(null);
  useEffect(() => { animatePaths(svgRef.current); }, []);
  return (
    <svg
      ref={svgRef}
      viewBox="0 0 600 380"
      fill="none"
      stroke={accent}
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-full h-auto"
      data-testid="hero-svg-survey"
    >
      {/* faint grid */}
      {Array.from({ length: 7 }).map((_, i) => (
        <line key={`g${i}`} x1="20" y1={40 + i * 50} x2="580" y2={40 + i * 50} stroke="rgba(255,255,255,0.08)" strokeDasharray="3,5" />
      ))}
      {Array.from({ length: 11 }).map((_, i) => (
        <line key={`gv${i}`} x1={40 + i * 52} y1="20" x2={40 + i * 52} y2="360" stroke="rgba(255,255,255,0.08)" strokeDasharray="3,5" />
      ))}

      {/* North arrow */}
      <g transform="translate(530 60)">
        <circle cx="0" cy="0" r="22" stroke={accentAlt} strokeOpacity="0.7" />
        <polygon points="0,-18 5,4 -5,4" fill={accentAlt} stroke={accentAlt} />
        <text x="0" y="-26" textAnchor="middle" fontSize="9" fill={accentAlt} fontWeight="700">N</text>
      </g>

      {/* Adjoiner line (top) */}
      <line x1="60" y1="80" x2="500" y2="80" stroke={accent} strokeOpacity="0.45" strokeDasharray="6,4" />
      <text x="280" y="74" fontSize="7" fill={accent} fillOpacity="0.7">ADJOINER PROPERTY LINE</text>

      {/* Boundary (residential lot) */}
      <polygon points="80,110 480,110 460,300 100,290" stroke={accentAlt} strokeWidth="1.6" fill="none" />
      {/* Bearings + distances */}
      <text x="270" y="105" fontSize="7" fill={accentAlt} fontWeight="700">N 89°15'12" E    400.00'</text>
      <text x="445" y="200" fontSize="7" fill={accentAlt} fontWeight="700" transform="rotate(95 460 200)">S 04°30'18" E  195.00'</text>
      <text x="260" y="308" fontSize="7" fill={accentAlt} fontWeight="700">N 88°40'05" W    365.00'</text>
      <text x="90" y="200" fontSize="7" fill={accentAlt} fontWeight="700" transform="rotate(-94 95 200)">N 03°50'40" W  185.00'</text>

      {/* Iron pins at corners */}
      {[[80, 110], [480, 110], [460, 300], [100, 290]].map((c, i) => (
        <circle key={`pin${i}`} cx={c[0]} cy={c[1]} r="3" fill={accentAlt} stroke="none" />
      ))}

      {/* Fence symbol (zig-zag along boundary) */}
      <polyline points="80,113 90,109 100,113 110,109 120,113 130,109 140,113 150,109 160,113 170,109 180,113 190,109 200,113 210,109 220,113 230,109 240,113 250,109 260,113 270,109 280,113 290,109 300,113 310,109 320,113 330,109 340,113 350,109 360,113 370,109 380,113 390,109 400,113 410,109 420,113 430,109 440,113 450,109 460,113 470,109 480,113" stroke={accent} strokeWidth="0.8" strokeOpacity="0.7" />
      <text x="370" y="124" fontSize="6" fill={accent} fillOpacity="0.8">─x─x─  6' WOODEN FENCE</text>

      {/* Building footprint */}
      <rect x="200" y="160" width="180" height="80" stroke={accent} strokeWidth="1.4" />
      <text x="290" y="205" textAnchor="middle" fontSize="9" fill="#fff" fontWeight="700">BLDG</text>
      <text x="290" y="220" textAnchor="middle" fontSize="6" fill={accent} fillOpacity="0.85">2,400 SF · FFE 102.5</text>

      {/* Driveway */}
      <polygon points="280,240 320,240 322,275 278,275" stroke={accent} strokeOpacity="0.85" fill="none" />
      <text x="300" y="265" textAnchor="middle" fontSize="6" fill={accent} fillOpacity="0.85">DRIVEWAY</text>

      {/* Walkway */}
      <polyline points="290,275 290,310 280,310" stroke={accent} strokeOpacity="0.6" strokeDasharray="2,2" />
      <text x="245" y="312" fontSize="6" fill={accent} fillOpacity="0.7">WALK</text>

      {/* Right of Way (road) */}
      <line x1="60" y1="335" x2="520" y2="335" stroke={accent} strokeOpacity="0.6" strokeDasharray="6,3" />
      <line x1="60" y1="365" x2="520" y2="365" stroke={accent} strokeOpacity="0.6" strokeDasharray="6,3" />
      <line x1="80" y1="350" x2="500" y2="350" stroke={accentAlt} strokeWidth="0.6" strokeDasharray="14,8" />
      <text x="60" y="332" fontSize="6" fill={accent} fillOpacity="0.6">R/W</text>
      <text x="495" y="332" fontSize="6" fill={accent} fillOpacity="0.6">R/W</text>
      <text x="240" y="378" fontSize="8" fill={accentAlt} fontWeight="700">MAIN STREET — 60' R/W</text>

      {/* Sidewalk */}
      <line x1="60" y1="325" x2="520" y2="325" stroke={accent} strokeOpacity="0.4" />
      <text x="60" y="320" fontSize="6" fill={accent} fillOpacity="0.5">SIDEWALK</text>

      {/* corner crops */}
      <polyline points="10,10 30,10 30,30" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="590,10 570,10 570,30" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="10,370 30,370 30,350" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="590,370 570,370 570,350" stroke={accentAlt} strokeOpacity="0.8" />
    </svg>
  );
};

const MepHero = ({ accent, accentAlt }) => {
  const svgRef = useRef(null);
  useEffect(() => { animatePaths(svgRef.current); }, []);
  return (
    <svg ref={svgRef} viewBox="0 0 600 380" fill="none" stroke={accent} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="w-full h-auto" data-testid="hero-svg-mep">
      {/* faint grid */}
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`g${i}`} x1="0" y1={40 + i * 40} x2="600" y2={40 + i * 40} stroke="rgba(255,255,255,0.07)" />
      ))}
      {/* HVAC duct (top) */}
      <rect x="60" y="60" width="480" height="22" stroke={accentAlt} strokeWidth="1.5" />
      <text x="280" y="76" fontSize="9" fill={accentAlt} fontWeight="700">HVAC SUPPLY DUCT 24×12"</text>
      {Array.from({ length: 6 }).map((_, i) => (
        <line key={`d${i}`} x1={100 + i * 75} y1="82" x2={100 + i * 75} y2="105" stroke={accentAlt} strokeWidth="2" />
      ))}
      {/* Diffusers */}
      {Array.from({ length: 6 }).map((_, i) => (
        <g key={`df${i}`} transform={`translate(${100 + i * 75} 110)`}>
          <rect x="-10" y="0" width="20" height="6" stroke={accent} />
          <line x1="-10" y1="3" x2="10" y2="3" stroke={accent} />
        </g>
      ))}
      {/* Plumbing pipes */}
      <line x1="60" y1="180" x2="540" y2="180" stroke={accent} strokeWidth="2" />
      <line x1="60" y1="200" x2="540" y2="200" stroke={accent} strokeWidth="2" strokeOpacity="0.6" />
      <text x="60" y="172" fontSize="8" fill={accent} fontWeight="700">CW · 2" CU</text>
      <text x="60" y="218" fontSize="8" fill={accent} fillOpacity="0.7" fontWeight="700">HW · 1½" CU</text>
      {/* T-fittings */}
      {[150, 280, 410].map((x, i) => (
        <g key={`t${i}`} transform={`translate(${x} 180)`}>
          <line x1="0" y1="0" x2="0" y2="40" stroke={accent} strokeWidth="1.5" />
          <circle cx="0" cy="0" r="3" fill={accent} />
        </g>
      ))}
      {/* Electrical conduit */}
      <line x1="60" y1="270" x2="540" y2="270" stroke={accentAlt} strokeWidth="1.6" strokeDasharray="8,3" />
      <text x="60" y="262" fontSize="8" fill={accentAlt} fontWeight="700">EMT ¾" · 12 AWG</text>
      {/* Panel + outlets */}
      <rect x="60" y="290" width="40" height="60" stroke={accentAlt} strokeWidth="1.5" />
      <text x="80" y="325" textAnchor="middle" fontSize="7" fill={accentAlt} fontWeight="700">MDP</text>
      {[180, 260, 340, 420, 500].map((x, i) => (
        <g key={`o${i}`} transform={`translate(${x} 320)`}>
          <line x1="0" y1="-50" x2="0" y2="0" stroke={accentAlt} strokeWidth="1" />
          <circle cx="0" cy="0" r="5" stroke={accentAlt} />
          <text y="14" textAnchor="middle" fontSize="6" fill={accentAlt}>RCP-{i + 1}</text>
        </g>
      ))}
      {/* legend */}
      <polyline points="10,10 30,10 30,30" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="590,370 570,370 570,350" stroke={accentAlt} strokeOpacity="0.8" />
    </svg>
  );
};

const BimHero = ({ accent, accentAlt }) => {
  const svgRef = useRef(null);
  useEffect(() => { animatePaths(svgRef.current); }, []);
  return (
    <svg ref={svgRef} viewBox="0 0 600 380" fill="none" stroke={accent} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="w-full h-auto" data-testid="hero-svg-bim">
      {/* iso grid */}
      {Array.from({ length: 16 }).map((_, i) => (
        <line key={`p${i}`} x1={i * 38} y1="380" x2={i * 38 + 280} y2="40" stroke="rgba(255,255,255,0.08)" />
      ))}
      {/* multi-floor BIM building */}
      {[0, 1, 2, 3, 4].map(f => {
        const y = 300 - f * 50;
        return (
          <g key={`fl${f}`}>
            <polygon points={`140,${y} 360,${y - 40} 460,${y} 360,${y + 30} 140,${y}`} stroke={f === 2 ? accentAlt : accent} strokeWidth="1.3" />
            <line x1="180" y1={y - 5} x2="180" y2={y + 20} stroke={accent} strokeOpacity="0.5" />
            <line x1="240" y1={y - 15} x2="240" y2={y + 10} stroke={accent} strokeOpacity="0.5" />
            <line x1="300" y1={y - 25} x2="300" y2={y + 0} stroke={accent} strokeOpacity="0.5" />
            <line x1="360" y1={y - 40} x2="360" y2={y + 30} stroke={accent} strokeOpacity="0.5" />
          </g>
        );
      })}
      <text x="200" y="65" fontSize="10" fill={accentAlt} fontWeight="700">LOD 400 · FEDERATED MODEL</text>
      <text x="80" y="345" fontSize="8" fill={accent}>5 FLOORS · CLASH FREE · IFC EXPORT READY</text>
      {/* corners */}
      <polyline points="10,10 30,10 30,30" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="590,370 570,370 570,350" stroke={accentAlt} strokeOpacity="0.8" />
    </svg>
  );
};

const StructuralHero = ({ accent, accentAlt }) => {
  const svgRef = useRef(null);
  useEffect(() => { animatePaths(svgRef.current); }, []);
  return (
    <svg ref={svgRef} viewBox="0 0 600 380" fill="none" stroke={accent} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="w-full h-auto" data-testid="hero-svg-structural">
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`g${i}`} x1="0" y1={40 + i * 40} x2="600" y2={40 + i * 40} stroke="rgba(255,255,255,0.07)" />
      ))}
      {/* W-shape steel beam (cross-section + side view) */}
      {/* Top flange */}
      <rect x="120" y="120" width="360" height="14" stroke={accentAlt} strokeWidth="1.6" />
      {/* Web */}
      <rect x="288" y="134" width="24" height="120" stroke={accent} strokeWidth="1.3" />
      {/* Bottom flange */}
      <rect x="120" y="254" width="360" height="14" stroke={accentAlt} strokeWidth="1.6" />
      <text x="120" y="113" fontSize="9" fill={accentAlt} fontWeight="700">W18×35 — BEAM B-1</text>
      <text x="500" y="195" fontSize="8" fill={accent} fontWeight="700">tw=¼"</text>
      <text x="305" y="200" fontSize="8" fill={accent} fontWeight="700" transform="rotate(-90 305 200)">d=18"</text>
      <text x="200" y="278" fontSize="8" fill={accent} fontWeight="700">bf=6"</text>

      {/* Bolted connection — left end plate */}
      <rect x="100" y="115" width="20" height="160" stroke={accentAlt} strokeWidth="1.4" />
      {[140, 175, 215, 250].map((y, i) => (
        <g key={`bl${i}`}>
          <circle cx="110" cy={y} r="3.5" stroke={accentAlt} strokeWidth="1.2" />
          <line x1="106" y1={y} x2="114" y2={y} stroke={accent} strokeWidth="0.7" />
          <line x1="110" y1={y - 4} x2="110" y2={y + 4} stroke={accent} strokeWidth="0.7" />
        </g>
      ))}
      <text x="60" y="200" fontSize="8" fill={accent} fontWeight="700" transform="rotate(-90 60 200)">8 × ¾"Ø A325</text>

      {/* Web stiffeners */}
      <line x1="220" y1="138" x2="220" y2="250" stroke={accentAlt} strokeWidth="1.2" />
      <line x1="380" y1="138" x2="380" y2="250" stroke={accentAlt} strokeWidth="1.2" />

      {/* Weld symbols */}
      <g transform="translate(290 120)">
        <line x1="-12" y1="0" x2="0" y2="0" stroke={accent} strokeWidth="1" />
        <polygon points="0,0 4,-6 8,0" fill={accent} stroke="none" />
        <text x="14" y="2" fontSize="7" fill={accent}>¼ FW</text>
      </g>
      <g transform="translate(290 264)">
        <line x1="-12" y1="0" x2="0" y2="0" stroke={accent} strokeWidth="1" />
        <polygon points="0,0 4,6 8,0" fill={accent} stroke="none" />
        <text x="14" y="9" fontSize="7" fill={accent}>¼ FW</text>
      </g>

      {/* Dimension lines */}
      <line x1="120" y1="305" x2="480" y2="305" stroke={accent} strokeOpacity="0.6" />
      <line x1="120" y1="300" x2="120" y2="310" stroke={accent} strokeOpacity="0.6" />
      <line x1="480" y1="300" x2="480" y2="310" stroke={accent} strokeOpacity="0.6" />
      <text x="280" y="320" fontSize="8" fill={accent} fontWeight="700">30'-0" SPAN</text>

      {/* corner crops */}
      <polyline points="10,10 30,10 30,30" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="590,370 570,370 570,350" stroke={accentAlt} strokeOpacity="0.8" />
    </svg>
  );
};

const GisHero = ({ accent, accentAlt }) => {
  const svgRef = useRef(null);
  useEffect(() => { animatePaths(svgRef.current); }, []);
  return (
    <svg ref={svgRef} viewBox="0 0 600 380" fill="none" stroke={accent} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="w-full h-auto" data-testid="hero-svg-gis">
      {Array.from({ length: 9 }).map((_, i) => (
        <line key={`g${i}`} x1="0" y1={40 + i * 36} x2="600" y2={40 + i * 36} stroke="rgba(255,255,255,0.06)" />
      ))}
      {/* Topographic contour rings — two hills */}
      {[
        { cx: 200, cy: 195, rs: [40, 70, 100, 130, 160], el: [820, 800, 780, 760, 740] },
        { cx: 430, cy: 220, rs: [30, 55, 80, 105, 130], el: [850, 830, 810, 790, 770] }
      ].map((hill, h) => (
        <g key={`hill${h}`}>
          {hill.rs.map((r, i) => (
            <ellipse
              key={`r${h}-${i}`}
              cx={hill.cx} cy={hill.cy}
              rx={r * 1.3} ry={r * 0.8}
              stroke={i % 2 === 0 ? accent : accentAlt}
              strokeOpacity={0.4 + i * 0.1}
              strokeWidth={i === 0 ? 1.4 : 0.8}
            />
          ))}
          <text x={hill.cx - 10} y={hill.cy - hill.rs[0] * 0.8 - 4} fontSize="8" fill={accentAlt} fontWeight="700">{hill.el[0]}'</text>
          <text x={hill.cx + hill.rs[2] * 0.9} y={hill.cy + 4} fontSize="7" fill={accent}>{hill.el[2]}'</text>
        </g>
      ))}

      {/* River / blue line */}
      <path d="M 60 320 Q 180 280 290 305 T 540 280" stroke="#3b82f6" strokeWidth="2" />
      <text x="200" y="298" fontSize="7" fill="#3b82f6" fontWeight="700">CREEK</text>

      {/* GIS markers */}
      {[[120, 110], [350, 95], [510, 130], [80, 230]].map((p, i) => (
        <g key={`mk${i}`} transform={`translate(${p[0]} ${p[1]})`}>
          <circle cx="0" cy="0" r="4" fill={accentAlt} stroke="none" />
          <circle cx="0" cy="0" r="10" stroke={accentAlt} strokeOpacity="0.5" />
          <text x="8" y="3" fontSize="7" fill={accentAlt} fontWeight="700">P{i + 1}</text>
        </g>
      ))}

      {/* Parcel boundary */}
      <polygon points="80,60 540,55 540,355 70,360" stroke={accent} strokeWidth="1.3" strokeDasharray="6,4" />
      <text x="90" y="50" fontSize="8" fill={accent} fontWeight="700">PARCEL · 124.5 ACRES</text>

      {/* Compass */}
      <g transform="translate(540 60)">
        <circle cx="0" cy="0" r="22" stroke={accentAlt} strokeOpacity="0.7" />
        <polygon points="0,-18 5,4 -5,4" fill={accentAlt} stroke={accentAlt} />
        <text x="0" y="-26" textAnchor="middle" fontSize="9" fill={accentAlt} fontWeight="700">N</text>
      </g>

      {/* corners */}
      <polyline points="10,10 30,10 30,30" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="590,370 570,370 570,350" stroke={accentAlt} strokeOpacity="0.8" />
    </svg>
  );
};

const PlantHero = ({ accent, accentAlt }) => {
  const svgRef = useRef(null);
  useEffect(() => { animatePaths(svgRef.current); }, []);
  return (
    <svg ref={svgRef} viewBox="0 0 600 380" fill="none" stroke={accent} strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" className="w-full h-auto" data-testid="hero-svg-plant">
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`g${i}`} x1="0" y1={40 + i * 40} x2="600" y2={40 + i * 40} stroke="rgba(255,255,255,0.07)" />
      ))}
      {/* Storage vessel V-101 */}
      <rect x="80" y="120" width="80" height="170" stroke={accentAlt} strokeWidth="1.5" />
      <ellipse cx="120" cy="120" rx="40" ry="14" stroke={accentAlt} />
      <ellipse cx="120" cy="290" rx="40" ry="14" stroke={accentAlt} />
      <text x="120" y="106" textAnchor="middle" fontSize="9" fill={accentAlt} fontWeight="700">V-101</text>
      <text x="120" y="215" textAnchor="middle" fontSize="7" fill={accent}>STORAGE</text>
      <text x="120" y="225" textAnchor="middle" fontSize="7" fill={accent}>1,200 GAL</text>

      {/* Process line v-101 → P-101 */}
      <line x1="160" y1="270" x2="240" y2="270" stroke={accent} strokeWidth="1.6" />
      <g transform="translate(195 270)">
        <polygon points="-6,-5 -6,5 6,0" fill={accent} stroke="none" />
        <text x="-14" y="-8" fontSize="7" fill={accent} fontWeight="700">2"-A1</text>
      </g>
      {/* Gate valve */}
      <g transform="translate(220 270)">
        <polygon points="-8,-7 -8,7 0,0" fill="none" stroke={accent} />
        <polygon points="8,-7 8,7 0,0" fill="none" stroke={accent} />
      </g>

      {/* Pump P-101 */}
      <circle cx="270" cy="270" r="20" stroke={accentAlt} strokeWidth="1.5" />
      <line x1="252" y1="270" x2="288" y2="270" stroke={accent} />
      <line x1="270" y1="252" x2="270" y2="288" stroke={accent} />
      <text x="270" y="306" textAnchor="middle" fontSize="8" fill={accentAlt} fontWeight="700">P-101</text>

      {/* Discharge to heat exchanger E-101 */}
      <line x1="290" y1="270" x2="370" y2="270" stroke={accent} strokeWidth="1.6" />
      <line x1="370" y1="270" x2="370" y2="180" stroke={accent} strokeWidth="1.6" />

      {/* Heat exchanger E-101 (shell-and-tube) */}
      <rect x="340" y="140" width="120" height="40" stroke={accentAlt} strokeWidth="1.5" />
      <line x1="350" y1="140" x2="350" y2="180" stroke={accent} strokeOpacity="0.6" />
      <line x1="450" y1="140" x2="450" y2="180" stroke={accent} strokeOpacity="0.6" />
      <line x1="345" y1="160" x2="455" y2="160" stroke={accent} strokeOpacity="0.5" strokeDasharray="3,2" />
      <text x="400" y="135" textAnchor="middle" fontSize="9" fill={accentAlt} fontWeight="700">E-101 · HEAT EX</text>

      {/* Instruments — pressure gauge */}
      <g transform="translate(220 230)">
        <circle cx="0" cy="0" r="14" stroke={accent} />
        <line x1="0" y1="0" x2="0" y2="-14" stroke={accent} />
        <text x="0" y="3" textAnchor="middle" fontSize="7" fill={accent} fontWeight="700">PI</text>
        <text x="0" y="-19" textAnchor="middle" fontSize="6" fill={accent}>101</text>
      </g>
      {/* TI */}
      <g transform="translate(400 110)">
        <circle cx="0" cy="0" r="14" stroke={accent} />
        <line x1="0" y1="14" x2="0" y2="30" stroke={accent} />
        <text x="0" y="3" textAnchor="middle" fontSize="7" fill={accent} fontWeight="700">TI</text>
        <text x="0" y="-2" textAnchor="middle" fontSize="6" fill={accent}>102</text>
      </g>

      {/* Reactor R-101 */}
      <rect x="490" y="220" width="70" height="100" stroke={accentAlt} strokeWidth="1.5" />
      <ellipse cx="525" cy="220" rx="35" ry="10" stroke={accentAlt} />
      <ellipse cx="525" cy="320" rx="35" ry="10" stroke={accentAlt} />
      <text x="525" y="208" textAnchor="middle" fontSize="9" fill={accentAlt} fontWeight="700">R-101</text>
      <text x="525" y="275" textAnchor="middle" fontSize="7" fill={accent}>REACTOR</text>
      <line x1="460" y1="180" x2="525" y2="180" stroke={accent} strokeWidth="1.4" />
      <line x1="525" y1="180" x2="525" y2="220" stroke={accent} strokeWidth="1.4" />

      <text x="60" y="350" fontSize="8" fill={accentAlt} fontWeight="700">P&amp;ID · UNIT 100 · REV C</text>
      <polyline points="10,10 30,10 30,30" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="590,370 570,370 570,350" stroke={accentAlt} strokeOpacity="0.8" />
    </svg>
  );
};

const CivilHero = ({ accent, accentAlt }) => {
  const svgRef = useRef(null);
  useEffect(() => { animatePaths(svgRef.current); }, []);
  return (
    <svg ref={svgRef} viewBox="0 0 600 380" fill="none" stroke={accent} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="w-full h-auto" data-testid="hero-svg-civil">
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`g${i}`} x1="0" y1={40 + i * 40} x2="600" y2={40 + i * 40} stroke="rgba(255,255,255,0.07)" strokeDasharray="3,4" />
      ))}
      {/* Road plan view */}
      <path d="M 30 230 Q 180 130 320 200 T 580 170" stroke={accent} strokeWidth="1.5" />
      <path d="M 30 270 Q 180 170 320 240 T 580 210" stroke={accent} strokeWidth="1.5" />
      {/* Centerline */}
      <path d="M 30 250 Q 180 150 320 220 T 580 190" stroke={accentAlt} strokeWidth="0.8" strokeDasharray="14,8" />
      <text x="180" y="118" fontSize="9" fill={accentAlt} fontWeight="700">STA 0+00 → STA 12+50</text>
      <text x="450" y="158" fontSize="8" fill={accent} fontWeight="700">R = 350&apos;</text>

      {/* Curb & gutter cross-section */}
      <line x1="60" y1="320" x2="540" y2="320" stroke={accent} strokeWidth="1.5" />
      <polyline points="60,320 60,308 110,308 110,320" stroke={accentAlt} strokeWidth="1.4" />
      <polyline points="540,320 540,308 490,308 490,320" stroke={accentAlt} strokeWidth="1.4" />
      <text x="270" y="316" textAnchor="middle" fontSize="8" fill={accent} fontWeight="700">PAVEMENT · 2% CROSS-SLOPE</text>
      <text x="80" y="304" fontSize="6" fill={accentAlt}>CURB &amp; GUTTER</text>

      {/* Storm drain inlet & utility */}
      <rect x="220" y="332" width="50" height="14" stroke={accent} strokeWidth="1.3" />
      <line x1="270" y1="339" x2="350" y2="339" stroke="#3b82f6" strokeWidth="1.6" />
      <text x="222" y="344" fontSize="6" fill={accent} fontWeight="700">STORM-IN</text>
      <text x="290" y="335" fontSize="6" fill="#3b82f6" fontWeight="700">15&quot; RCP</text>
      <circle cx="360" cy="339" r="4" stroke={accentAlt} strokeWidth="1.2" />
      <text x="370" y="343" fontSize="6" fill={accentAlt}>MH</text>

      {/* Spot elevations */}
      <text x="80" y="220" fontSize="7" fill={accentAlt} fontWeight="700">EL 142.50</text>
      <text x="280" y="190" fontSize="7" fill={accentAlt} fontWeight="700">EL 144.10</text>
      <text x="500" y="160" fontSize="7" fill={accentAlt} fontWeight="700">EL 145.80</text>

      {/* North + stationing */}
      <g transform="translate(540 60)">
        <circle cx="0" cy="0" r="22" stroke={accentAlt} strokeOpacity="0.7" />
        <polygon points="0,-18 5,4 -5,4" fill={accentAlt} stroke={accentAlt} />
        <text x="0" y="-26" textAnchor="middle" fontSize="9" fill={accentAlt} fontWeight="700">N</text>
      </g>

      <polyline points="10,10 30,10 30,30" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="590,370 570,370 570,350" stroke={accentAlt} strokeOpacity="0.8" />
    </svg>
  );
};

const ManufacturingHero = ({ accent, accentAlt }) => {
  const svgRef = useRef(null);
  useEffect(() => { animatePaths(svgRef.current); }, []);
  return (
    <svg ref={svgRef} viewBox="0 0 600 380" fill="none" stroke={accent} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="w-full h-auto" data-testid="hero-svg-manufacturing">
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`g${i}`} x1="0" y1={40 + i * 40} x2="600" y2={40 + i * 40} stroke="rgba(255,255,255,0.07)" />
      ))}
      {/* Title block */}
      <text x="40" y="58" fontSize="9" fill={accentAlt} fontWeight="700">BRACKET — PART 2034-B · MAT&apos;L A36</text>

      {/* Front-view: machined bracket */}
      <polygon points="80,120 280,120 280,260 230,260 230,180 130,180 130,260 80,260" stroke={accent} strokeWidth="1.4" />
      {/* Mounting holes */}
      {[[110, 145], [250, 145], [110, 240], [250, 240]].map((c, i) => (
        <g key={`h${i}`}>
          <circle cx={c[0]} cy={c[1]} r="6" stroke={accentAlt} strokeWidth="1.2" />
          <line x1={c[0] - 9} y1={c[1]} x2={c[0] + 9} y2={c[1]} stroke={accentAlt} strokeOpacity="0.6" strokeDasharray="2,2" />
          <line x1={c[0]} y1={c[1] - 9} x2={c[0]} y2={c[1] + 9} stroke={accentAlt} strokeOpacity="0.6" strokeDasharray="2,2" />
        </g>
      ))}
      <text x="98" y="138" fontSize="6" fill={accentAlt}>Ø.50</text>

      {/* Dimension lines */}
      <line x1="80" y1="280" x2="280" y2="280" stroke={accent} strokeOpacity="0.7" />
      <line x1="80" y1="275" x2="80" y2="285" stroke={accent} strokeOpacity="0.7" />
      <line x1="280" y1="275" x2="280" y2="285" stroke={accent} strokeOpacity="0.7" />
      <text x="180" y="294" textAnchor="middle" fontSize="8" fill={accent} fontWeight="700">8.00</text>
      <line x1="295" y1="120" x2="295" y2="260" stroke={accent} strokeOpacity="0.7" />
      <line x1="290" y1="120" x2="300" y2="120" stroke={accent} strokeOpacity="0.7" />
      <line x1="290" y1="260" x2="300" y2="260" stroke={accent} strokeOpacity="0.7" />
      <text x="305" y="195" fontSize="8" fill={accent} fontWeight="700">5.50</text>

      {/* Side / iso view */}
      <polygon points="360,140 500,120 500,240 360,260" stroke={accentAlt} strokeWidth="1.3" />
      <polygon points="360,140 380,130 520,110 500,120" stroke={accent} strokeOpacity="0.6" />
      <polygon points="500,120 520,110 520,230 500,240" stroke={accent} strokeOpacity="0.6" />
      <text x="430" y="105" textAnchor="middle" fontSize="8" fill={accentAlt} fontWeight="700">ISO VIEW</text>

      {/* GD&amp;T frame */}
      <rect x="60" y="320" width="140" height="22" stroke={accent} strokeOpacity="0.7" />
      <line x1="92" y1="320" x2="92" y2="342" stroke={accent} strokeOpacity="0.7" />
      <line x1="135" y1="320" x2="135" y2="342" stroke={accent} strokeOpacity="0.7" />
      <text x="76" y="337" textAnchor="middle" fontSize="9" fill={accent} fontWeight="700">⊥</text>
      <text x="113" y="337" textAnchor="middle" fontSize="9" fill={accent} fontWeight="700">.005</text>
      <text x="168" y="337" textAnchor="middle" fontSize="9" fill={accent} fontWeight="700">A</text>

      {/* Surface finish symbol */}
      <g transform="translate(380 300)">
        <polyline points="0,0 8,-14 16,0" stroke={accentAlt} strokeWidth="1.2" />
        <text x="20" y="-2" fontSize="7" fill={accentAlt}>Ra 32 µin</text>
      </g>

      <polyline points="10,10 30,10 30,30" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="590,370 570,370 570,350" stroke={accentAlt} strokeOpacity="0.8" />
    </svg>
  );
};

const GraphicsHero = ({ accent, accentAlt }) => {
  const svgRef = useRef(null);
  useEffect(() => { animatePaths(svgRef.current); }, []);
  return (
    <svg ref={svgRef} viewBox="0 0 600 380" fill="none" stroke={accent} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="w-full h-auto" data-testid="hero-svg-graphics">
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`g${i}`} x1="0" y1={40 + i * 40} x2="600" y2={40 + i * 40} stroke="rgba(255,255,255,0.06)" />
      ))}

      {/* Render frame / canvas */}
      <rect x="80" y="60" width="440" height="240" stroke={accentAlt} strokeWidth="1.6" />
      <text x="80" y="54" fontSize="9" fill={accentAlt} fontWeight="700">FRAME · 1920 × 1080 · 4K READY</text>
      {/* Composition rule-of-thirds */}
      <line x1="226" y1="60" x2="226" y2="300" stroke={accent} strokeOpacity="0.3" strokeDasharray="3,3" />
      <line x1="374" y1="60" x2="374" y2="300" stroke={accent} strokeOpacity="0.3" strokeDasharray="3,3" />
      <line x1="80" y1="140" x2="520" y2="140" stroke={accent} strokeOpacity="0.3" strokeDasharray="3,3" />
      <line x1="80" y1="220" x2="520" y2="220" stroke={accent} strokeOpacity="0.3" strokeDasharray="3,3" />

      {/* Sun + atmosphere */}
      <circle cx="420" cy="120" r="22" stroke={accentAlt} strokeOpacity="0.85" />
      <circle cx="420" cy="120" r="32" stroke={accentAlt} strokeOpacity="0.45" />
      <circle cx="420" cy="120" r="42" stroke={accentAlt} strokeOpacity="0.20" />

      {/* Building silhouette */}
      <polygon points="120,260 120,170 180,170 180,140 240,140 240,200 300,200 300,160 360,160 360,260" stroke={accent} strokeWidth="1.3" />
      {/* Building windows */}
      {[[140, 200], [200, 215], [260, 220], [320, 200]].map((p, i) => (
        <rect key={`w${i}`} x={p[0]} y={p[1]} width="14" height="20" stroke={accent} strokeOpacity="0.55" />
      ))}

      {/* Foreground tree */}
      <line x1="430" y1="260" x2="430" y2="225" stroke={accent} strokeWidth="1.3" />
      <circle cx="430" cy="215" r="16" stroke={accent} strokeWidth="1.2" />
      <circle cx="430" cy="215" r="10" stroke={accent} strokeOpacity="0.5" />

      {/* Ground line */}
      <line x1="80" y1="260" x2="520" y2="260" stroke={accent} strokeOpacity="0.7" />

      {/* Color swatch palette */}
      <g transform="translate(80 320)">
        {['#94a3b8', '#f97316', '#3b82f6', '#a855f7', '#facc15', '#ef4444', '#0ea5e9', '#94a3b8'].map((c, i) => (
          <rect key={`sw${i}`} x={i * 28} y="0" width="22" height="22" fill={c} stroke="rgba(255,255,255,0.4)" strokeWidth="0.6" />
        ))}
        <text x="0" y="-6" fontSize="7" fill={accent} fontWeight="700">PALETTE · 8 SWATCHES</text>
      </g>

      {/* Render label */}
      <g transform="translate(380 320)">
        <rect x="0" y="0" width="120" height="22" stroke={accentAlt} />
        <text x="60" y="15" textAnchor="middle" fontSize="9" fill={accentAlt} fontWeight="700">V-RAY · 4K · 240 FPS</text>
      </g>

      <polyline points="10,10 30,10 30,30" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="590,370 570,370 570,350" stroke={accentAlt} strokeOpacity="0.8" />
    </svg>
  );
};

const SignageHero = ({ accent, accentAlt }) => {
  const svgRef = useRef(null);
  useEffect(() => { animatePaths(svgRef.current); }, []);
  return (
    <svg ref={svgRef} viewBox="0 0 600 380" fill="none" stroke={accent} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="w-full h-auto" data-testid="hero-svg-signage">
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`g${i}`} x1="0" y1={40 + i * 40} x2="600" y2={40 + i * 40} stroke="rgba(255,255,255,0.07)" />
      ))}

      {/* Monolith pylon sign */}
      <rect x="80" y="80" width="120" height="220" stroke={accentAlt} strokeWidth="1.5" />
      <rect x="92" y="92" width="96" height="60" stroke={accent} strokeWidth="1.2" />
      <text x="140" y="128" textAnchor="middle" fontSize="14" fill={accentAlt} fontWeight="800">LD</text>
      <rect x="92" y="160" width="96" height="40" stroke={accent} strokeOpacity="0.7" />
      <line x1="100" y1="178" x2="180" y2="178" stroke={accent} strokeOpacity="0.6" />
      <rect x="92" y="208" width="96" height="40" stroke={accent} strokeOpacity="0.7" />
      <line x1="100" y1="226" x2="180" y2="226" stroke={accent} strokeOpacity="0.6" />
      <text x="140" y="275" textAnchor="middle" fontSize="7" fill={accent} fontWeight="700">ILLUMINATED PYLON</text>

      {/* Wayfinding directional sign */}
      <polygon points="260,140 460,140 480,170 460,200 260,200" stroke={accentAlt} strokeWidth="1.4" />
      <text x="360" y="176" textAnchor="middle" fontSize="11" fill={accentAlt} fontWeight="700">RECEPTION →</text>
      <text x="260" y="132" fontSize="7" fill={accent}>DIRECTIONAL · ADA-COMPLIANT</text>

      {/* ADA braille indicator */}
      {Array.from({ length: 6 }).map((_, i) => (
        <circle key={`d${i}`} cx={285 + (i % 3) * 6} cy={220 + Math.floor(i / 3) * 6} r="1.2" fill={accent} stroke="none" />
      ))}
      <text x="310" y="226" fontSize="7" fill={accent} fontWeight="700">BRAILLE — TACTILE</text>

      {/* LED light strip schematic */}
      <line x1="260" y1="250" x2="500" y2="250" stroke={accentAlt} strokeDasharray="4,3" />
      {Array.from({ length: 14 }).map((_, i) => (
        <circle key={`led${i}`} cx={266 + i * 17} cy="250" r="2.5" fill={accentAlt} stroke="none" />
      ))}
      <text x="380" y="266" textAnchor="middle" fontSize="7" fill={accentAlt} fontWeight="700">RGB LED STRIP · 24V</text>

      {/* Foundation footing */}
      <rect x="60" y="300" width="160" height="14" stroke={accent} strokeOpacity="0.7" />
      <text x="70" y="312" fontSize="6" fill={accent} fontWeight="700">CONC FTG 36×24×18</text>

      {/* Dimension */}
      <line x1="80" y1="328" x2="200" y2="328" stroke={accent} strokeOpacity="0.7" />
      <text x="140" y="342" textAnchor="middle" fontSize="8" fill={accent} fontWeight="700">8&apos;-0&quot; TALL</text>

      <polyline points="10,10 30,10 30,30" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="590,370 570,370 570,350" stroke={accentAlt} strokeOpacity="0.8" />
    </svg>
  );
};

const MillworkHero = ({ accent, accentAlt }) => {
  const svgRef = useRef(null);
  useEffect(() => { animatePaths(svgRef.current); }, []);
  return (
    <svg ref={svgRef} viewBox="0 0 600 380" fill="none" stroke={accent} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="w-full h-auto" data-testid="hero-svg-millwork">
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`g${i}`} x1="0" y1={40 + i * 40} x2="600" y2={40 + i * 40} stroke="rgba(255,255,255,0.07)" />
      ))}

      {/* Cabinet elevation */}
      <rect x="80" y="80" width="280" height="220" stroke={accentAlt} strokeWidth="1.6" />
      <line x1="220" y1="80" x2="220" y2="300" stroke={accentAlt} strokeWidth="1" />
      {/* Drawer fronts */}
      <rect x="90" y="90" width="120" height="60" stroke={accent} strokeWidth="1.1" />
      <rect x="90" y="156" width="120" height="60" stroke={accent} strokeWidth="1.1" />
      <rect x="90" y="222" width="120" height="68" stroke={accent} strokeWidth="1.1" />
      {/* Door */}
      <rect x="230" y="90" width="120" height="200" stroke={accent} strokeWidth="1.1" />
      <rect x="244" y="104" width="92" height="170" stroke={accent} strokeOpacity="0.5" />
      {/* Hardware (pulls / knobs) */}
      <line x1="142" y1="115" x2="158" y2="115" stroke={accentAlt} strokeWidth="2" />
      <line x1="142" y1="180" x2="158" y2="180" stroke={accentAlt} strokeWidth="2" />
      <line x1="142" y1="252" x2="158" y2="252" stroke={accentAlt} strokeWidth="2" />
      <circle cx="335" cy="192" r="3" fill={accentAlt} stroke="none" />

      {/* Wood grain texture */}
      {Array.from({ length: 6 }).map((_, i) => (
        <path key={`gr${i}`} d={`M 244 ${112 + i * 28} Q 290 ${110 + i * 28} 336 ${114 + i * 28}`} stroke={accent} strokeOpacity="0.20" />
      ))}

      <text x="80" y="74" fontSize="9" fill={accentAlt} fontWeight="700">BASE CABINET · OAK · STAIN: NATURAL</text>

      {/* Section view */}
      <g transform="translate(400 90)">
        <rect x="0" y="0" width="160" height="180" stroke={accent} strokeWidth="1.2" />
        <line x1="0" y1="20" x2="160" y2="20" stroke={accent} strokeOpacity="0.6" />
        <line x1="0" y1="60" x2="160" y2="60" stroke={accent} strokeOpacity="0.6" />
        <line x1="0" y1="120" x2="160" y2="120" stroke={accent} strokeOpacity="0.6" />
        <text x="80" y="14" textAnchor="middle" fontSize="7" fill={accent} fontWeight="700">COUNTERTOP — 1¼&quot;</text>
        <text x="80" y="40" textAnchor="middle" fontSize="6" fill={accent} fontWeight="700">DRAWER BOX — ¾&quot;</text>
        <text x="80" y="95" textAnchor="middle" fontSize="6" fill={accent} fontWeight="700">SHELF — ¾&quot; ADJ</text>
        <text x="80" y="155" textAnchor="middle" fontSize="6" fill={accent} fontWeight="700">TOE-KICK 4&quot;</text>
        {/* Hatch (concrete floor) */}
        {Array.from({ length: 8 }).map((_, i) => (
          <line key={`h${i}`} x1={i * 22} y1="180" x2={i * 22 + 14} y2="195" stroke={accent} strokeOpacity="0.5" />
        ))}
      </g>
      <text x="480" y="80" textAnchor="middle" fontSize="9" fill={accentAlt} fontWeight="700">SECTION A-A</text>

      {/* Dimension */}
      <line x1="80" y1="318" x2="360" y2="318" stroke={accent} strokeOpacity="0.7" />
      <line x1="80" y1="313" x2="80" y2="323" stroke={accent} strokeOpacity="0.7" />
      <line x1="360" y1="313" x2="360" y2="323" stroke={accent} strokeOpacity="0.7" />
      <text x="220" y="332" textAnchor="middle" fontSize="9" fill={accent} fontWeight="700">3&apos;-0&quot; W × 7&apos;-4&quot; H</text>

      <polyline points="10,10 30,10 30,30" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="590,370 570,370 570,350" stroke={accentAlt} strokeOpacity="0.8" />
    </svg>
  );
};

const GeotechHero = ({ accent, accentAlt }) => {
  const svgRef = useRef(null);
  useEffect(() => { animatePaths(svgRef.current); }, []);
  return (
    <svg ref={svgRef} viewBox="0 0 600 380" fill="none" stroke={accent} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="w-full h-auto" data-testid="hero-svg-geotech">
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={`g${i}`} x1="0" y1={40 + i * 40} x2="600" y2={40 + i * 40} stroke="rgba(255,255,255,0.06)" />
      ))}

      {/* Soil profile bounding */}
      <rect x="60" y="70" width="480" height="260" stroke={accentAlt} strokeWidth="1.4" />
      {/* Surface terrain */}
      <path d="M 60 90 Q 200 70 360 95 T 540 105" stroke={accentAlt} strokeWidth="1.5" />
      <text x="200" y="62" fontSize="9" fill={accentAlt} fontWeight="700">SOIL CROSS-SECTION · BORING BH-1 / BH-2</text>

      {/* Stratum 1 — topsoil */}
      <path d="M 60 130 Q 200 115 360 138 T 540 145" stroke={accent} strokeWidth="1" />
      <text x="68" y="115" fontSize="7" fill={accent} fontWeight="700">TOPSOIL (0&apos;-2&apos;)</text>
      {/* Stratum 2 — sandy clay */}
      <path d="M 60 180 Q 200 160 360 188 T 540 190" stroke={accent} strokeWidth="1" />
      <text x="68" y="164" fontSize="7" fill={accent} fontWeight="700">SANDY CLAY (2&apos;-8&apos;)</text>
      {/* Stratum 3 — silty sand */}
      <path d="M 60 240 Q 200 220 360 248 T 540 250" stroke={accent} strokeWidth="1" />
      <text x="68" y="218" fontSize="7" fill={accent} fontWeight="700">SILTY SAND (8&apos;-18&apos;)</text>
      {/* Stratum 4 — bedrock */}
      <text x="68" y="280" fontSize="7" fill={accent} fontWeight="700">WEATHERED ROCK</text>

      {/* Hatches per stratum */}
      {Array.from({ length: 24 }).map((_, i) => (
        <line key={`h1-${i}`} x1={62 + i * 20} y1="105" x2={70 + i * 20} y2="125" stroke={accent} strokeOpacity="0.30" />
      ))}
      {Array.from({ length: 20 }).map((_, i) => (
        <circle key={`h2-${i}`} cx={70 + i * 24} cy={170} r="1.5" fill={accent} fillOpacity="0.4" stroke="none" />
      ))}
      {Array.from({ length: 22 }).map((_, i) => (
        <line key={`h3-${i}`} x1={70 + i * 22} y1="210" x2={86 + i * 22} y2="240" stroke={accent} strokeOpacity="0.30" />
      ))}
      {Array.from({ length: 26 }).map((_, i) => (
        <line key={`h4-${i}`} x1={62 + i * 18} y1="270" x2={70 + i * 18} y2="320" stroke={accentAlt} strokeOpacity="0.35" />
      ))}

      {/* Borehole logs */}
      <g transform="translate(160 80)">
        <line x1="0" y1="0" x2="0" y2="240" stroke={accentAlt} strokeWidth="1.6" strokeDasharray="3,3" />
        <text x="-12" y="-4" fontSize="8" fill={accentAlt} fontWeight="700">BH-1</text>
        {[20, 60, 110, 170, 220].map((y, i) => (
          <g key={`sp${i}`} transform={`translate(0 ${y})`}>
            <line x1="-6" y1="0" x2="6" y2="0" stroke={accentAlt} strokeWidth="1.4" />
            <text x="10" y="3" fontSize="6" fill={accentAlt}>N={[4, 8, 12, 24, 50][i]}</text>
          </g>
        ))}
      </g>
      <g transform="translate(380 80)">
        <line x1="0" y1="0" x2="0" y2="240" stroke={accentAlt} strokeWidth="1.6" strokeDasharray="3,3" />
        <text x="-12" y="-4" fontSize="8" fill={accentAlt} fontWeight="700">BH-2</text>
        {[30, 70, 120, 175, 215].map((y, i) => (
          <g key={`sp2-${i}`} transform={`translate(0 ${y})`}>
            <line x1="-6" y1="0" x2="6" y2="0" stroke={accentAlt} strokeWidth="1.4" />
            <text x="10" y="3" fontSize="6" fill={accentAlt}>N={[5, 10, 14, 28, 50][i]}</text>
          </g>
        ))}
      </g>

      {/* Groundwater */}
      <line x1="60" y1="200" x2="540" y2="200" stroke="#3b82f6" strokeWidth="1.2" strokeDasharray="2,4" />
      <text x="468" y="196" fontSize="7" fill="#3b82f6" fontWeight="700">GROUNDWATER ▽</text>

      {/* Footing foundation */}
      <rect x="270" y="100" width="60" height="14" stroke={accent} strokeWidth="1.3" />
      <text x="300" y="98" textAnchor="middle" fontSize="6" fill={accent} fontWeight="700">PROPOSED FOOTING</text>

      <polyline points="10,10 30,10 30,30" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="590,370 570,370 570,350" stroke={accentAlt} strokeOpacity="0.8" />
    </svg>
  );
};

const DefaultSkylineHero = ({ accent, accentAlt, density }) => {
  const svgRef = useRef(null);
  useEffect(() => { animatePaths(svgRef.current); }, []);
  const rects = [];
  for (let i = 0; i < density; i++) {
    const x = 30 + i * (440 / density);
    const w = 440 / density - 3;
    const h = 60 + ((i * 37) % 160);
    rects.push({ x, y: 260 - h, w, h, accent: i % 5 === 0 });
  }
  return (
    <svg ref={svgRef} viewBox="0 0 500 320" fill="none" stroke={accent} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="w-full h-auto" data-testid="hero-svg-default">
      {Array.from({ length: 7 }).map((_, i) => (
        <line key={`g${i}`} x1="10" y1={50 + i * 32} x2="490" y2={50 + i * 32} stroke="rgba(255,255,255,0.10)" strokeDasharray="3,3" />
      ))}
      <line x1="0" y1="265" x2="500" y2="265" stroke={accent} strokeOpacity="0.6" />
      <circle cx="430" cy="80" r="36" stroke={accentAlt} strokeOpacity="0.7" />
      <circle cx="430" cy="80" r="22" stroke={accentAlt} strokeOpacity="0.5" />
      {rects.map((r, i) => (
        <g key={`b${i}`}>
          <rect x={r.x} y={r.y} width={r.w} height={r.h} stroke={r.accent ? accentAlt : accent} strokeOpacity={r.accent ? 0.95 : 0.7} />
          {Array.from({ length: Math.max(2, Math.floor(r.h / 18)) }).map((_, k) => (
            <line key={`w${i}-${k}`} x1={r.x + 1.5} y1={r.y + 8 + k * 14} x2={r.x + r.w - 1.5} y2={r.y + 8 + k * 14} stroke={accent} strokeOpacity="0.35" />
          ))}
        </g>
      ))}
      <polyline points="0,20 20,20 20,0" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="500,20 480,20 480,0" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="0,300 20,300 20,320" stroke={accentAlt} strokeOpacity="0.8" />
      <polyline points="500,300 480,300 480,320" stroke={accentAlt} strokeOpacity="0.8" />
    </svg>
  );
};

const BlueprintHero = ({
  variant,
  slug,
  accent = '#94a3b8',
  accentAlt = '#f97316',
  density = 24,
  className = ''
}) => {
  const v = variant || VARIANT_FOR_SLUG[slug] || 'default';
  const KNOWN = ['survey', 'mep', 'bim', 'structural', 'gis', 'plant', 'civil', 'manufacturing', 'graphics', 'signage', 'millwork', 'geotech'];
  return (
    <div className={`bp-hero relative ${className}`} data-testid="blueprint-hero" data-variant={v}>
      {v === 'survey' && <SurveyHero accent={accent} accentAlt={accentAlt} />}
      {v === 'mep' && <MepHero accent={accent} accentAlt={accentAlt} />}
      {v === 'bim' && <BimHero accent={accent} accentAlt={accentAlt} />}
      {v === 'structural' && <StructuralHero accent={accent} accentAlt={accentAlt} />}
      {v === 'gis' && <GisHero accent={accent} accentAlt={accentAlt} />}
      {v === 'plant' && <PlantHero accent={accent} accentAlt={accentAlt} />}
      {v === 'civil' && <CivilHero accent={accent} accentAlt={accentAlt} />}
      {v === 'manufacturing' && <ManufacturingHero accent={accent} accentAlt={accentAlt} />}
      {v === 'graphics' && <GraphicsHero accent={accent} accentAlt={accentAlt} />}
      {v === 'signage' && <SignageHero accent={accent} accentAlt={accentAlt} />}
      {v === 'millwork' && <MillworkHero accent={accent} accentAlt={accentAlt} />}
      {v === 'geotech' && <GeotechHero accent={accent} accentAlt={accentAlt} />}
      {!KNOWN.includes(v) && <DefaultSkylineHero accent={accent} accentAlt={accentAlt} density={density} />}
      <style>{`
        @keyframes bp-draw { to { stroke-dashoffset: 0; } }
        .bp-hero svg path,.bp-hero svg line,.bp-hero svg polyline,.bp-hero svg polygon,.bp-hero svg rect,.bp-hero svg circle,.bp-hero svg ellipse {
          will-change: stroke-dashoffset;
        }
      `}</style>
    </div>
  );
};

export default BlueprintHero;
